/**
 * Les trois réponses à une offre de cours sur UNE porte du serveur : l'accepter (« Je candidate »,
 * « Je confirme que j'accepte »), la masquer, retirer sa candidature. Les trois écrivent un statut
 * (`accepted`, `rejected`, `rejected`) — c'est ce que `ProposalsTabScreen`, `GroupAttributionProposalScreen`
 * et `SubstitutionProposalScreen` envoient.
 *
 * Une écriture par réponse (`useApiMutation`), chacune son geste mesuré (`offer.accept`, `offer.hide`,
 * `offer.withdraw`) : `useApiMutation` nomme son geste à la création, pas à l'appel. La tranche est la
 * même, la garde « est-ce NOUS » de chaque écriture dit laquelle a répondu. Une surface ne crée que les
 * réponses qu'elle offre (`useTutorOfferAnswer`). Le paramètre de portée (`id`) sert aux portes d'une
 * liste, où une réponse peut venir pour une autre ligne.
 */
import { type GestureName } from "../../../hooks/gestureTracking"
import { type ApiService } from "../../../hooks/useApiFetch"
import { useApiMutation } from "../../../hooks/useApiMutation"

export type TutorOfferAnswer = "accept" | "hide" | "withdraw"

/** Le statut que chaque réponse écrit. */
const OFFER_ANSWER_STATUS: Record<TutorOfferAnswer, "accepted" | "rejected"> = {
    accept: "accepted",
    hide: "rejected",
    withdraw: "rejected",
}

const GESTURE: Record<TutorOfferAnswer, GestureName> = {
    accept: "offer.accept",
    hide: "offer.hide",
    withdraw: "offer.withdraw",
}

type Callbacks = {
    onSuccess: (answer: TutorOfferAnswer, data: unknown) => void
    onError: (answer: TutorOfferAnswer, message: string) => void
}

const FALLBACK = "Ta réponse n'a pas pu être envoyée. Réessaie dans un instant."

/** Une réponse sur une porte ; `start(routeParams, body)` rend `false` si rien ne part. */
export function useTutorOfferAnswer(
    answer: TutorOfferAnswer,
    service: ApiService,
    surface: string,
    scopeParam: string | undefined,
    callbacks: Callbacks
) {
    const mutation = useApiMutation({
        service,
        gesture: GESTURE[answer],
        surface,
        scopeParam,
        fallback: FALLBACK,
        skipWhilePending: true,
        onSuccess: (data) => callbacks.onSuccess(answer, data),
        onError: (message) => callbacks.onError(answer, message),
    })
    return {
        start: (routeParams: Record<string, unknown>, body: Record<string, unknown> = {}) =>
            mutation.start(routeParams, { ...body, status: OFFER_ANSWER_STATUS[answer] }),
        /** L'ouverture de la feuille qui mène à la réponse se mesure sur le même geste. */
        opened: () => mutation.gesture.opened(),
    }
}

/** Les trois réponses sur une porte ; `start(answer, routeParams, body)` rend `false` si rien ne part. */
export function useTutorOfferAnswers(
    service: ApiService,
    surface: string,
    scopeParam: string | undefined,
    callbacks: Callbacks
) {
    const byAnswer = {
        accept: useTutorOfferAnswer("accept", service, surface, scopeParam, callbacks),
        hide: useTutorOfferAnswer("hide", service, surface, scopeParam, callbacks),
        withdraw: useTutorOfferAnswer("withdraw", service, surface, scopeParam, callbacks),
    }
    return {
        start: (answer: TutorOfferAnswer, routeParams: Record<string, unknown>, body: Record<string, unknown> = {}) =>
            byAnswer[answer].start(routeParams, body),
        opened: (answer: TutorOfferAnswer) => byAnswer[answer].opened(),
    }
}
